import React, { useEffect, useState } from 'react'
import { Tab } from '@headlessui/react'
import { useIntl, defineMessage } from 'react-intl'
import { Headline, Icons, Button } from 'msp-components'
import FormActionButtons from '@/components/molecules/FormActionButtons/FormActionButtons'
import AddressFields from '@/components/molecules/AddressFields/AddressFields'
import { PackstationAddressFields } from '@/components/molecules/PackstationAddressFields'
import type { IconName as IconNameType } from 'msp-integrations'
import { useCookie } from 'msp-integrations'
import * as T from './AddressCreateEditForm.types'
import * as S from './AddressCreateEditForm.styles'

const AddressCreateEditForm = ({
  title,
  hasSavedAddresses,
  hideSubmitButton,
  form,
  toggleAddressEdit,
  saveButtonLabel,
  submitButtonLabel,
  isPackstationAddress,
  isEditingMode,
  hidePackstationTab = false,
  isLocker
}: T.AddressCreateEditFormProps) => {
  const { getCookieAllowedById, openConsentBanner } = useCookie()
  const { formatMessage } = useIntl()
  const [selectedTab, setSelectedTab] = useState(0)
  const defaultSaveButtonLabel = defineMessage({
    defaultMessage: 'Save & Continue the Checkout',
    id: 'msp.address_form.button.save_and_continue'
  })

  const isGoogleMapsAllowed = getCookieAllowedById('s1104') // CONSENTS['s1104'] = 'google_maps'

  const tabs: { title: string; iconName: IconNameType }[] = [
    {
      title: formatMessage({
        defaultMessage: 'Address',
        id: 'msp.address_form.tab.address'
      }),
      iconName: 'House'
    },
    {
      title: formatMessage({
        defaultMessage: 'DHL Packstation',
        id: 'msp.address_form.tab.dhl_packstation'
      }),
      iconName: 'Building'
    }
  ]

  const handleTabChange = (index: number) => {
    form?.reset()
    if (!isEditingMode) {
      setSelectedTab(index)
    } else {
      setSelectedTab(isPackstationAddress ? 1 : 0)
    }
  }

  useEffect(() => {
    setSelectedTab(isEditingMode && isPackstationAddress ? 1 : 0)
  }, [])

  return (
    <div className={S.MainWrapper}>
      <div className={S.MainContentWrapper}>
        {hasSavedAddresses && <Headline tag={'h6'}>{title}</Headline>}
        <div className={S.ViewPortContentWrapper}>
          <Tab.Group selectedIndex={selectedTab} onChange={handleTabChange}>
            <Tab.List className={S.TabListWrapper}>
              {tabs.map((tab, key) => {
                if (hidePackstationTab && key === 1) return null
                const disabled =
                  isEditingMode && key === (isPackstationAddress ? 0 : 1)
                return (
                  <Tab
                    key={`${tab.iconName}${key}`}
                    disabled={disabled}
                    className={({ selected }) =>
                      `${S.TabWrapper} ${
                        selected ? S.TabSelectedWrapper : S.TabNoStateWrapper
                      } ${disabled && S.TabDisabledWrapper}`
                    }
                  >
                    <>
                      <div>
                        <Icons
                          iconName={tab.iconName}
                          height='25px'
                          width='26px'
                        />
                      </div>
                      <div className={S.TabTileWrapper}>{tab.title}</div>
                    </>
                  </Tab>
                )
              })}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                {selectedTab === 0 && <AddressFields form={form} />}
              </Tab.Panel>
              {!hidePackstationTab && (
                <Tab.Panel>
                  {selectedTab === 1 &&
                    (isGoogleMapsAllowed ? (
                      <PackstationAddressFields
                        form={form}
                        isLocker={isLocker}
                      />
                    ) : (
                      <div className='flex flex-row flex-wrap items-center justify-center px-5 pb-10 text-center'>
                        {formatMessage({
                          id: 'msp.address_form.packstation.cookie_not_allowed_message',
                          defaultMessage:
                            'In order to use the Packstation finder, please adjust your cookie settings'
                        })}
                        <button
                          aria-label='Open the cookie modal'
                          className='mx-1 underline'
                          onClick={() => openConsentBanner(true)}
                        >
                          {formatMessage({
                            id: 'msp.address_form.packstation.open_cookie_modal_btn_text',
                            defaultMessage: 'here.'
                          })}
                        </button>
                      </div>
                    ))}
                </Tab.Panel>
              )}
            </Tab.Panels>
          </Tab.Group>

          {hasSavedAddresses && !hideSubmitButton ? (
            <FormActionButtons
              saveButtonProps={{
                isDisabled: form?.formState?.isSubmitting
              }}
              saveButtonLabel={formatMessage(
                saveButtonLabel || defaultSaveButtonLabel
              )}
              onCancel={toggleAddressEdit}
            />
          ) : (
            !hideSubmitButton &&
            submitButtonLabel && (
              <Button
                isSubmit
                className={S.StandardSubmitButton}
                isDisabled={form?.formState?.isSubmitting}
              >
                {formatMessage(submitButtonLabel)}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default AddressCreateEditForm
